import { create } from 'zustand';

export type DeepLinkSource = 'appsflyer' | 'native';

export type DeepLinkIntent = {
  pathname: string;
  params?: Record<string, string>;
  source: DeepLinkSource;
  timestamp: number;
};

type DeepLinkStore = {
  pendingLink: DeepLinkIntent | undefined;

  /**
   * Store a new pending intent. Overwrites any existing one (only one link at a time).
   * @param link - The deep link intent to store
   * @returns void
   */
  setPendingLink: (link: DeepLinkIntent) => void;

  /**
   * Consume the pending link: reads it and clears it in one step.
   * Returns the intent that was stored, or undefined if nothing was pending.
   * @returns DeepLinkIntent | undefined
   */
  consumePendingLink: () => DeepLinkIntent | undefined;

  /**
   * Get the actual pending link or undefined if there's no pending link
   * @returns DeepLinkIntent | undefined
   */
  getPendingLink: () => DeepLinkIntent | undefined;

  /**
   * Clear the pending link without consuming it
   * @returns void
   */
  clearPendingLink: () => void;
};

// ─── Store ────────────────────────────────────────────────────────────────────
export const useDeepLinkStore = create<DeepLinkStore>((set, get) => ({
  pendingLink: undefined,

  setPendingLink: (link) => {
    set({ pendingLink: link });
  },

  consumePendingLink: () => {
    const link = get().pendingLink;
    if (!link) return undefined;

    set({ pendingLink: undefined });

    return link;
  },

  getPendingLink: () => {
    const link = get().pendingLink;
    return link;
  },

  clearPendingLink: () => {
    set({ pendingLink: undefined });
  }
}));

// ─── Imperative API (usable outside React) ───────────────────────────────────
// These mirror the store actions but can be called from module-scope callbacks
// like appsFlyer.onDeepLink() which are registered before components mount.

export const setPendingLink = (link: DeepLinkIntent) => useDeepLinkStore.getState().setPendingLink(link);
export const consumePendingLink = () => useDeepLinkStore.getState().consumePendingLink();
export const getPendingLink = () => useDeepLinkStore.getState().getPendingLink();
export const clearPendingLink = () => useDeepLinkStore.getState().clearPendingLink();
